import { useMemo, useState } from 'react';
import { Outlet, useLocation, matchPath } from 'react-router-dom';
import { NavigationBar } from '@/components/NavigationBar';
import { AuthGuard } from '@/components/AuthGuard/AuthGuard';
import { EventFeedPanel } from '@/features/EventFeed/EventFeedPanel';
import { PipPlayerHost } from '@/features/PipPlayer';
import { QueryPanel } from '@/features/QueryPanel';
import {
    selectFeedPanelVisible,
    useEventFeedStore,
} from '@/stores/event-feed.store';
import { SidePanelSlotsContext } from '@/components/SidePanel';
import { AppRoutes } from '@/constants/routes';
import { cn } from '@/lib/utils';

const SIDE_SLOT_CLASSES =
    'relative w-[clamp(200px,15vw,300px)] flex-shrink-0 min-h-0 @container/panel';

const ROUTES_WITHOUT_COMPACT_QUERY_PANEL = [
    AppRoutes.HOME,
    AppRoutes.ALERT_RULES_CREATE,
    AppRoutes.ALERT_RULES_EDIT,
    AppRoutes.ALERT_RULE,
    AppRoutes.ALERT_RULE_ALERTS,
];

export default function Layout() {
    const requested = useEventFeedStore((state) => state.requested);
    const panelVisible = useEventFeedStore(selectFeedPanelVisible);
    const playerIsFullscreen = useEventFeedStore(
        (state) => state.playerIsFullscreen,
    );
    const { pathname } = useLocation();
    const [leftSlot, setLeftSlot] = useState<HTMLDivElement | null>(null);
    const [rightSlot, setRightSlot] = useState<HTMLDivElement | null>(null);
    const slots = useMemo(
        () => ({ left: leftSlot, right: rightSlot }),
        [leftSlot, rightSlot],
    );

    const showCompactQueryPanel = !ROUTES_WITHOUT_COMPACT_QUERY_PANEL.some(
        (route) => matchPath(route, pathname),
    );

    return (
        <AuthGuard>
            <div className="h-screen overflow-hidden flex flex-col bg-grey-900">
                <NavigationBar />
                <main className="flex-1 min-h-0 flex flex-col px-4 pb-4">
                    <div className="bg-grey-700 rounded-lg border border-grey-600 flex-1 min-h-0 flex flex-col px-3 panels:px-6 pb-8">
                        <SidePanelSlotsContext.Provider value={slots}>
                            <div className="flex-1 min-h-0 flex gap-6">
                                <div
                                    ref={setLeftSlot}
                                    className={cn(
                                        SIDE_SLOT_CLASSES,
                                        playerIsFullscreen && 'invisible',
                                    )}
                                />
                                <div className="relative flex-1 min-w-0 min-h-0 @container">
                                    <Outlet />

                                    {showCompactQueryPanel &&
                                        !playerIsFullscreen && (
                                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 z-50 w-full px-4 flex justify-center pointer-events-none [&>*]:pointer-events-auto">
                                                <QueryPanel compact />
                                            </div>
                                        )}
                                </div>
                                <div
                                    ref={setRightSlot}
                                    className={cn(
                                        SIDE_SLOT_CLASSES,
                                        playerIsFullscreen && 'invisible',
                                    )}
                                >
                                    {requested && !panelVisible && (
                                        <div className="hidden" aria-hidden>
                                            <EventFeedPanel />
                                        </div>
                                    )}
                                    {panelVisible && (
                                        <div className="h-full animate-in fade-in slide-in-from-right-10 duration-[250ms] ease-in-out motion-reduce:animate-none">
                                            <EventFeedPanel />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </SidePanelSlotsContext.Provider>
                    </div>

                    <PipPlayerHost />
                </main>
            </div>
        </AuthGuard>
    );
}
